import React from 'react';

import { Checkbox as HeadlessCheckbox, Field, Label } from '@headlessui/react';

import MinusIcon from '~/core/iconComponents/icons/MinusIcon';
import OkIcon from '~/core/iconComponents/icons/OkIcon';
import cn from '~/utils/cn';

export type CheckboxSize = 'sm' | 'md' | 'lg';
export type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

export interface CheckboxProps {
  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;
  /**
   * Whether the checkbox is in an indeterminate state
   */
  indeterminate?: boolean;
  /**
   * Whether the checkbox is disabled
   */
  disabled?: boolean;
  /**
   * The size of the checkbox
   */
  size?: CheckboxSize;
  /**
   * Label text for the checkbox
   */
  label?: string;
  /**
   * Description text for the checkbox
   */
  description?: string;
  /**
   * Error message to display when invalid
   */
  errorMessage?: string;
  /**
   * Whether the checkbox has a validation error
   */
  invalid?: boolean;
  /**
   * The name attribute for the checkbox
   */
  name?: string;
  /**
   * The value attribute for the checkbox
   */
  value?: string;
  /**
   * Handler to call when the checkbox state changes
   */
  onChange?: (checked: boolean) => void;
  /**
   * Additional CSS classes to apply to the checkbox
   */
  className?: string;
  /**
   * ARIA label for accessibility
   */
  ariaLabel?: string;
  /**
   * ARIA described by for accessibility
   */
  ariaDescribedby?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  indeterminate = false,
  disabled = false,
  size = 'sm',
  label,
  description,
  errorMessage,
  invalid = false,
  name,
  value,
  onChange,
  className = '',
  ariaLabel,
  ariaDescribedby,
}) => {
  // Size configurations
  const sizeClasses: Record<CheckboxSize, string> = {
    sm: 'h-[18px] w-[18px]',
    md: 'h-20 w-20',
    lg: 'h-24 w-24',
  };

  const iconSizeClasses: Record<CheckboxSize, string> = {
    sm: 'h-[18px] w-[18px]',
    md: 'h-20 w-20',
    lg: 'h-24 w-24',
  };

  // Base styles for the checkbox
  const baseStyles = cn(
    'group relative flex items-center justify-center rounded-sm border-2 transition-colors cursor-pointer',
    // focus styles
    'focus:outline-none focus:ring-2 focus:ring-current focus:ring-offset-2',
    // hover styles
    'hover:data-checked:brightness-90',
    // checked styles
    'data-checked:bg-current',
    sizeClasses[size],
    {
      // Normal state
      'border-grey-200 bg-white hover:border-current': !checked && !indeterminate && !disabled,
      // indeterminate state
      'bg-current': indeterminate && !disabled,
      // Disabled state
      'border-grey-200 bg-grey-100 cursor-not-allowed opacity-50': disabled,
      // Invalid state
      'border-red-700 focus:ring-red-700': invalid,
      // Disabled + checked state
      'border-grey-300 bg-grey-300': disabled && (checked || indeterminate),
    }
  );

  const iconStyles = cn('text-white transition-opacity shrink-0 px-2 stroke-[1.1]', iconSizeClasses[size], {
    'opacity-100': (checked || indeterminate) && !disabled,
    'opacity-0': !(checked || indeterminate) && !disabled,
    // 'opacity-30': disabled && (checked || indeterminate),
  });

  const labelStyles = cn('text-sm font-medium cursor-pointer', {
    'text-grey-900': !disabled,
    'text-grey-400 cursor-not-allowed': disabled,
    'text-red-900': invalid,
  });

  const descriptionStyles = cn('text-sm', {
    'text-grey-500': !invalid,
    'text-red-600': invalid,
  });

  const handleChange = (isChecked: boolean) => {
    if (!disabled && onChange) {
      onChange(isChecked);
    }
  };

  const checkboxElement = (
    <HeadlessCheckbox
      checked={checked}
      onChange={handleChange}
      disabled={disabled}
      name={name}
      value={value}
      className={cn('text-pink-600', baseStyles, className)}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
    >
      {checked || indeterminate ? (
        indeterminate ? (
          <MinusIcon className={iconStyles} />
        ) : (
          <OkIcon className={iconStyles} />
        )
      ) : null}
    </HeadlessCheckbox>
  );

  if (label || description || errorMessage) {
    return (
      <Field disabled={disabled} className="flex flex-col">
        <div className="flex items-center gap-8">
          {checkboxElement}
          {label && (
            <Label className={labelStyles} onClick={() => !disabled && handleChange(!checked)}>
              {label}
            </Label>
          )}
        </div>
        {description && !invalid && <p className={cn(descriptionStyles, 'mt-1')}>{description}</p>}
        {invalid && errorMessage && <p className={cn(descriptionStyles, 'mt-1')}>{errorMessage}</p>}
      </Field>
    );
  }

  return checkboxElement;
};

export default Checkbox;
